import React, {useEffect, useState} from "react";
import {
  IAvailableFilter,
  ProductList,
  ProductListFilterItemBoolean,
  ProductListFilterItemRange,
  ProductListFilterItemSelect,
  ProductListFilterItemType,
  ProductListFilterItemValueType,
} from "@connox/typescript-library";
import {useProductListContext} from "../ProductListProvider/ProductListProvider";
import ProductFilterSelect from "./ProductFilterSelect/ProductFilterSelect";
import AppToggleContainer from "./AppToggleContainer/AppToggleContainer";
import ProductFilterBoolean from "./ProductFilterBoolean/ProductFilterBoolean";
import ProductSortInput from "./ProductSortInput/ProductSortInput";
import ProductFilterRange from "./ProductFilterRange/ProductFilterRange";
import styles from "./ProductListFilter.module.scss";
import appToggleStyles from "./AppToggleContainer/AppToggleContainer.module.scss";
import {useTranslation} from "react-i18next";
import selectStyles from "../../lib/form/Select/Select.module.scss";
import nlOptionsFormStyles from "../../newsletter/NewsLetterOptionsForm.module.scss";
import FilterReview from "./FilterReview/FilterReview";
import {useMediaQuery} from "react-responsive";
import Drawer from "../../lib/form/Drawer/Drawer";
import Spinner from "../../utils/loader/Spinner";

/**
 * Commponent to render the filter and sort buttons
 * @constructor
 */

const ProductListFilter: React.FC = () => {
  const {t} = useTranslation();
  const {state, dispatch} = useProductListContext();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [mobileFilters, setMobileFilters] = useState<{[k: string]: ProductListFilterItemValueType}>({});
  const [isRendering, setIsRendering] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const isMobile = useMediaQuery({query: "(max-width: 480px)"});

  const toggleFilter = (): void => {
    dispatch({type: "SHOW_FILTERS", payload: !state.showFilters});
    setDrawerOpen(!isDrawerOpen);
  };

  useEffect(() => {
    if (!state.loading) {
      setDrawerOpen(false);
      setIsDirty(false);
    }
  }, [state.loading]);

  useEffect(() => {
    if (isMobile) {
      setMobileFilters(state.filters);
    }
  }, [JSON.stringify(state.filters)]);

  useEffect(() => {
    const instance = state.instance;
    if (!instance || !isMobile) {
      return;
    }

    const availableRangeFiltersFields = Array.from(instance.filter.filterItems).reduce(
      (acc: string[], [key, keyValue]) => {
        if (keyValue.type === ProductListFilterItemType.range) {
          acc.push(key);
        }

        return acc;
      },
      [],
    );

    availableRangeFiltersFields.forEach((key) => {
      const filtersNoKey = Object.assign({}, mobileFilters);
      delete filtersNoKey[key];

      const instanceCopy = Object.assign(Object.create(Object.getPrototypeOf(instance)), instance) as ProductList;

      instanceCopy.filter.applyFilterValue(filtersNoKey);
      instanceCopy.limit = 1;
      instanceCopy.offset = 0;

      const setRangeLimits = (filter: IAvailableFilter) => {
        dispatch({
          type: "SET_RANGE_FILTERS_VALUES",
          payload: {
            field: filter.field,
            value: {
              min: Math.floor(Number(filter.minValue ?? 0)),
              max: Math.ceil(Number(filter.maxValue ?? 0)),
            },
          },
        });
      };

      void instanceCopy.send().then((data) => {
        data.availableFilters.forEach((filter) => {
          if (filter.type === ProductListFilterItemType.range && filter.field === key) {
            setRangeLimits(filter);
          }
        });
      });
    });
  }, [JSON.stringify(mobileFilters)]);

  useEffect(() => {
    if (state.total > ProductList.PRODUCT_LIST_DEFAULT_LIMIT || Object.keys(state.filters).length || state.loading) {
      setIsRendering(true);

      return;
    }

    setIsRendering(false);
  }, [state.total]);

  // prevents rendering of filters for PLPs with max. 16 items
  if (!isRendering) {
    return null;
  }

  /**
   * Array of filter buttons, all of their corresponding `filterItem.type`
   */
  const filterItemArray = () => {
    const tempFilterItems: JSX.Element[] = [];
    // this is done to render boolean filters at the end of the list according to the ux requirement
    const tempBooleanFilterItems: JSX.Element[] = [];

    if (state.instance) {
      Array.from(state.instance.filter.filterItems.values())
        .filter((value) => value.values.size && value.count)
        .forEach((filterItem) => {
          switch (filterItem.type) {
            case ProductListFilterItemType.select:
              tempFilterItems.push(
                <ProductFilterSelect
                  item={filterItem as ProductListFilterItemSelect}
                  key={filterItem.field}
                  onSelect={(values) => {
                    if (isMobile) {
                      let newMobileFilters = Object.assign({}, mobileFilters);
                      if (!values.length) {
                        delete newMobileFilters[filterItem.field];
                      } else {
                        newMobileFilters = {...mobileFilters, [filterItem.field]: values};
                      }
                      setMobileFilters(newMobileFilters);
                      setIsDirty(true);
                    }
                  }}
                />,
              );
              break;
            case ProductListFilterItemType.boolean:
              tempBooleanFilterItems.push(
                <ProductFilterBoolean
                  item={filterItem as ProductListFilterItemBoolean}
                  key={filterItem.field}
                  onSelect={(value) => {
                    if (isMobile) {
                      let newMobileFilters = Object.assign({}, mobileFilters);
                      if (!value) {
                        delete newMobileFilters[filterItem.field];
                      } else {
                        newMobileFilters = {...mobileFilters, [filterItem.field]: value};
                      }
                      setMobileFilters(newMobileFilters);
                      setIsDirty(true);
                    }
                  }}
                />,
              );
              break;
            case ProductListFilterItemType.range:
              tempFilterItems.push(
                <ProductFilterRange
                  item={filterItem as ProductListFilterItemRange}
                  key={filterItem.field}
                  onSelect={(value) => {
                    if (isMobile) {
                      let newMobileFilters = Object.assign({}, mobileFilters);
                      const limit = state.rangeFilterMap?.get(filterItem.field);
                      if (value.max === limit?.max && value.min === limit?.min) {
                        delete newMobileFilters[filterItem.field];
                      } else {
                        newMobileFilters = {...mobileFilters, [filterItem.field]: value};
                      }
                      setMobileFilters(newMobileFilters);
                      setIsDirty(true);
                    }
                  }}
                />,
              );
              break;
          }
        });
    }

    return [tempFilterItems, tempBooleanFilterItems];
  };

  // pre-call of the filterItemsArray() function, just to get the length of the result
  const [filterItems, booleanFilterItems] = filterItemArray();
  const filterLength = filterItems.length + booleanFilterItems.length;

  // mobile layout
  if (isMobile) {
    const filterNumbers = (): number => {
      return Object.entries(state.filters).reduce((acc, [, value]) => {
        if (Array.isArray(value)) {
          return acc + value.length;
        } else {
          // case boolean or range filter are set
          return acc + 1;
        }
      }, 0);
    };
    const reset = () => {
      dispatch({type: "RESET"});
      dispatch({type: "TRIGGER"});
      setMobileFilters({});
      setDrawerOpen(false);
    };

    const setFilters = () => {
      if (!state.loading) {
        dispatch({
          type: "SET_LIMIT",
          payload: ProductList.PRODUCT_LIST_DEFAULT_LIMIT,
        });
        dispatch({
          type: "SET_FILTERS",
          payload: {filters: mobileFilters, doUrlParamUpdate: true},
        });
      }
    };

    return (
      <>
        <div className={styles.filterWrapperMobile}>
          <div>{t("{{quantity}} Artikel", {quantity: state.total})}</div>
          <div className={styles.filterButton} onClick={toggleFilter}>
            <span className={styles.icon} />
            <span>{t("Filtern & Sortieren") + (filterNumbers() > 0 ? ` (${filterNumbers()})` : "")}</span>
          </div>
        </div>
        <Drawer open={isDrawerOpen} onClose={toggleFilter}>
          <div className={styles.drawerContent}>
            <div className={styles.sectionTitle}>{t("Sortieren nach")}</div>
            <ProductSortInput />
            <div className={styles.sectionTitle}>{t("Filtern")}</div>
            <div className={styles.filterContainer}>{[...filterItems, ...booleanFilterItems]}</div>
            <div className={styles.buttonsWrapper}>
              <button className={`${styles.button} ${styles.submit}`} onClick={setFilters} disabled={!isDirty}>
                {state.loading ? <Spinner /> : <span>{t("Filter anwenden")}</span>}
              </button>
              <button className={`${styles.button}`} onClick={reset}>
                {t("Filter zurücksetzen")}
              </button>
            </div>
          </div>
        </Drawer>
        <FilterReview />
      </>
    );
  }

  // desktop layout
  return (
    <React.Fragment>
      <div className={styles.filterContainer}>
        {(filterLength > 0 || state.loading) && (
          <AppToggleContainer onClick={toggleFilter} isOpening={state.showFilters} icon={"filter"}>
            {t("Filter")}
          </AppToggleContainer>
        )}
        {filterLength === 0 && !state.loading && <BackButton />}
        <ProductSortInput />
      </div>
      {state.showFilters && (
        <div className={styles.filterContainer}>
          <div className={styles.filterItemsContainer}>{filterItems}</div>
          <div className={styles.filterItemsContainer}>{booleanFilterItems}</div>
        </div>
      )}
      <FilterReview />
    </React.Fragment>
  );
};

export default ProductListFilter;

const BackButton: React.FC = () => {
  const {t} = useTranslation();

  return (
    <div className={appToggleStyles.item}>
      <div
        className={[
          selectStyles.wrapper,
          nlOptionsFormStyles.select,
          appToggleStyles.formElementContainer,
          appToggleStyles.revertArrow,
        ].join(" ")}
        onClick={() => window.history.back()}
      >
        <b>{t("Zurück")}</b>
      </div>
    </div>
  );
};
